import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import type {
  AmbientNarrativeContest,
  AmbientNarrativeContestResolution,
  AmbientRecoveredNote,
  QRAnswerItem,
} from '@scripsteam/scrips-signal-ds';
import {
  EMPTY_RECOVERIES,
  contestByKey,
  toContestViews,
  toNoteViews,
  withCollisions,
  withLanded,
  withPersisted,
  withResolution,
  withUndone,
  type AmbientRecoveries,
} from './ambient-recoveries';
import type { NarrativeCollision } from './to-narrative-answers';
import {
  ENCOUNTER_NARRATIVE_LINK_ID,
  appendReversal,
  readSink,
  sinkAnswer,
  type OrphanEntry,
} from './orphan-sink';

/** The one slice of the host's response this hook writes. Structural, like `TemplateLike`. */
interface ResponseLike {
  answers: QRAnswerItem[];
}

export interface UseAmbientRecoveriesOptions<R extends ResponseLike> {
  /**
   * The encounter is signed. EVERY write below refuses while it is true (PROD-2547 / B5, the same
   * host guard `handleAmbientNarratives` carries), and the views withhold the controls.
   */
  signed: boolean;
  setResponse: Dispatch<SetStateAction<R>>;
  /** An edit happened (PROD-2700: bumps the page's dirty generation). */
  markDirty: () => void;
}

/**
 * The host half of PROD-2445 and PROD-2447: holds what ambient produced that has no proposal card,
 * performs the two writes a clinician can ask for, and hands the panel the rows to show.
 *
 * Kept out of `encounter-shell-page.tsx` so the page gains call sites, not logic: the transitions
 * live in `ambient-recoveries.ts` and are tested there.
 */
export function useAmbientRecoveries<R extends ResponseLike>({
  signed,
  setResponse,
  markDirty,
}: UseAmbientRecoveriesOptions<R>) {
  const [state, setState] = useState<AmbientRecoveries>(EMPTY_RECOVERIES);

  /** Collisions from one Soap frame — computed by the host against its current answers. */
  const noteCollisions = useCallback((collisions: readonly NarrativeCollision[]) => {
    if (collisions.length === 0) return;
    setState((prev) => withCollisions(prev, collisions));
  }, []);

  /** Lines that just landed in the sink (`landingEntries`), so each gets a card. */
  const noteLanded = useCallback((landed: readonly OrphanEntry[]) => {
    if (landed.length === 0) return;
    setState((prev) => withLanded(prev, landed));
  }, []);

  /** A save resolved carrying these answers. */
  const notePersisted = useCallback((savedAnswers: readonly QRAnswerItem[]) => {
    setState((prev) => withPersisted(prev, savedAnswers));
  }, []);

  /**
   * The clinician chose. "Keep mine" writes nothing and records the choice, so the same wording is
   * not asked again. "Use dictated" replaces that one answer, marked `aiAuthored` — the only path by
   * which a dictated narrative ever displaces an answer, and it is a click.
   */
  const resolveContest = useCallback(
    (contest: AmbientNarrativeContest, resolution: AmbientNarrativeContestResolution) => {
      if (signed) return;
      const collision = contestByKey(state, contest.id);
      if (!collision) return;
      setState((prev) => withResolution(prev, collision.key, resolution));
      if (resolution !== 'use-dictated') return;
      setResponse((prev) => ({
        ...prev,
        answers: [
          ...prev.answers.filter((a) => a.linkId !== collision.linkId),
          { linkId: collision.linkId, value: collision.dictated, aiAuthored: true },
        ],
      }));
      markDirty();
    },
    [signed, state, setResponse, markDirty],
  );

  /** Undo appends a reversal to the sink — never deletes the original landing. */
  const undoNote = useCallback(
    (note: AmbientRecoveredNote) => {
      if (signed) return;
      const tracked = state.notes.find((n) => n.id === note.id);
      if (!tracked || tracked.status === 'undone') return;
      setResponse((prev) => {
        const existing = readSink(prev.answers);
        const next = appendReversal(existing, tracked.text);
        if (next === existing || next === undefined) return prev;
        return {
          ...prev,
          answers: [...prev.answers.filter((a) => a.linkId !== ENCOUNTER_NARRATIVE_LINK_ID), sinkAnswer(next)],
        };
      });
      markDirty();
      setState((prev) => withUndone(prev, note.id));
    },
    [signed, state, setResponse, markDirty],
  );

  const narrativeContests = useMemo(() => toContestViews(state, signed), [state, signed]);
  const recoveredNotes = useMemo(() => toNoteViews(state, signed), [state, signed]);

  return {
    narrativeContests,
    recoveredNotes,
    noteCollisions,
    noteLanded,
    notePersisted,
    resolveContest,
    undoNote,
  };
}
